/**
 * VHAKACHA ADVERTISING
 * ============================================================
 * How an advert becomes something rendered.
 *
 * WHY THIS EXISTS
 * ---------------
 * An advert may BIND to a listing (`source.kind === 'LISTING'`), and
 * when it does the listing owns the price and the location. Folding
 * the two together is therefore not a display detail — it decides
 * what price a traveller is promised. A copied price is a hardcoded
 * price with extra steps: the hero would promise $189 while the
 * booking form charges the listing's current $240.
 *
 * MIRRORED, BYTE-FOR-BEHAVIOUR, WITH:
 *   provider app  →  src/lib/advertising.ts
 *
 * A slot string or source kind one repo does not know about renders
 * nothing, silently. Edit both.
 * ============================================================
 */

export const AD_SLOTS = ['HOME_CAROUSEL', 'HOME_BANNER', 'DESTINATION_BANNER', 'SEARCH_BANNER'];

export const DEFAULT_CTA_LABEL = 'More Details';

/** Adverts bound to a listing take their price and location from it. */
export function isListingBound(source) {
  return source?.kind === 'LISTING';
}

export function boundListingId(ad) {
  return isListingBound(ad?.source) ? ad.source.listingId : null;
}

/**
 * Where an advert's CTA points.
 *
 * `/listing/:id` exists because the listing detail moved out of a modal and
 * onto a route. Before that there was nowhere to send anyone — which is why
 * the seven hardcoded carousel slides all carried the same copy-pasted id and
 * no click handler at all.
 */
export function adHref(source) {
  if (!source) return null;
  switch (source.kind) {
    case 'LISTING':
      return `/listing/${source.listingId}`;
    case 'LOCATION':
      return `/destination/${encodeURIComponent(source.location)}`;
    case 'EXTERNAL':
      return source.url || null;
    case 'HOUSE':
    default:
      return null;
  }
}

/** Is this advert inside its flight window? Both bounds are optional. */
export function isInFlight(ad, now = new Date()) {
  const at = now.getTime();

  if (ad?.startAt) {
    const start = Date.parse(ad.startAt);
    // A malformed date must not silently take the advert live, or dark. It is
    // ignored, and `status` remains the gate.
    if (!Number.isNaN(start) && at < start) return false;
  }
  if (ad?.endAt) {
    const end = Date.parse(ad.endAt);
    if (!Number.isNaN(end) && at > end) return false;
  }
  return true;
}

/**
 * Fold an advert and its bound listing into what gets rendered.
 *
 * Returns null when a bound listing is genuinely gone — the advert then drops
 * out of its slot, which is what you want when an operator archives the
 * product being advertised.
 *
 * ⚠️ CALLERS MUST DISTINGUISH "gone" FROM "not loaded yet" — see `selectAds`.
 */
export function resolveAd(ad, listing) {
  if (isListingBound(ad?.source) && !listing) return null;

  const bound = isListingBound(ad?.source) ? listing : null;

  return {
    ...ad,
    imageUrl: ad.imageUrl ?? bound?.images?.[0] ?? null,
    imageAlt: ad.imageAlt ?? bound?.name ?? ad.captionTitle ?? '',
    captionTitle: ad.captionTitle ?? bound?.name ?? null,
    captionSubtitle: ad.captionSubtitle ?? bound?.location ?? null,

    // Price comes off the LIVE listing whenever there is one, never off the
    // advert. This is the whole reason the fold exists.
    cost: bound ? bound.cost : ad.cost ?? null,
    currency: bound ? bound.currency : ad.currency ?? null,
    priceUnit: bound ? bound.priceUnit : ad.priceUnit ?? null,

    href: adHref(ad.source),
    external: ad.source?.kind === 'EXTERNAL',
    ctaLabel: ad.ctaLabel || DEFAULT_CTA_LABEL,
  };
}

/**
 * A listing presented as though it were an advert.
 *
 * The banner slots have always shown SOMETHING — until now, a listing picked
 * at random from whatever the page was displaying. That house inventory is
 * worth keeping when a slot is unsold, but it must go through the same fold
 * as a real advert, because the alternative is what shipped: BannerDynamic
 * rendered a hardcoded `from $200pp` on every destination page, for every
 * listing, regardless of what the listing actually costs.
 *
 * `subtitle` is the listing's category — the line a real advert fills with
 * its `tagline`.
 */
export function adFromListing(listing) {
  if (!listing) return null;

  const resolved = resolveAd(
    {
      id: `listing-${listing.id}`,
      status: 'ACTIVE',
      priority: 0,
      source: { kind: 'LISTING', listingId: listing.id },
      advertiserName: listing.company?.name || listing.name || '',
      createdAt: '',
    },
    listing
  );

  return resolved && { ...resolved, tagline: listing.type || null, house: true };
}

/**
 * The adverts to render in one slot, in order.
 *
 * @param options.listingsById   bound listings, keyed by id
 * @param options.listingsLoaded whether the listings snapshot has arrived.
 *   When false, bound adverts are KEPT rather than dropped — "we do not know
 *   yet" is not "deleted". Without this the hero renders its fallback on
 *   every cold load and then visibly swaps.
 */
export function selectAds(adverts, slot, options = {}) {
  const {
    location = null,
    listingsById = null,
    listingsLoaded = true,
    now = new Date(),
  } = options;

  const matching = (adverts || []).filter((ad) => {
    if (!ad || ad.slot !== slot) return false;
    if (ad.status !== 'ACTIVE') return false;
    if (!isInFlight(ad, now)) return false;

    // An advert with no location restriction runs everywhere.
    if (location && Array.isArray(ad.showOnLocations) && ad.showOnLocations.length > 0) {
      if (!ad.showOnLocations.includes(location)) return false;
    }
    return true;
  });

  const resolved = matching.map((ad) => {
    const listingId = boundListingId(ad);

    if (listingId && !listingsLoaded) {
      // Hold it. Resolving now would drop it for the wrong reason.
      return resolveAd({ ...ad, source: { kind: 'HOUSE' } }, null);
    }
    return resolveAd(ad, listingId ? listingsById?.[listingId] ?? null : null);
  });

  return resolved
    .filter(Boolean)
    .sort((a, b) => {
      const byPriority = (b.priority ?? 0) - (a.priority ?? 0);
      if (byPriority !== 0) return byPriority;
      return String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
    });
}
